import { TagHandling } from "services";
import {
  WeightedKeyword,
  CategoryConfig,
  ContentScore,
  ContentWithMetadata,
  Category,
  Tag,
} from "types";

export interface ContentAnalysisResult {
  categoryScores: Map<string, number>;
  tagScores: Map<string, number>;
  mainCategory: [string, number];
  mainTagGroup: [string, number];
}

export class ContentAnalysis<T extends ContentWithMetadata> {
  constructor(
    private readonly tagHandling: TagHandling,
    private readonly enhancedCategoryKeywords: Record<string, CategoryConfig>
  ) {}

  private getTimeInDays(date: Date | null | undefined): number {
    return date?.getTime() ?? 0;
  }

  private toDate(
    dateInput: string | Date | null | undefined
  ): Date | null | undefined {
    if (!dateInput) return null;
    return dateInput instanceof Date ? dateInput : new Date(dateInput);
  }

  private hasWordMatch(text: string, word: string): boolean {
    const regex = new RegExp(`\\b${word}\\b`, "i");
    return regex.test(text);
  }

  private analyzeCategoryScores(
    content: string,
    categories: Category[]
  ): {
    categoryScores: Map<string, number>;
    mainCategory: [string, number];
  } {
    const text = content.toLowerCase();
    const sentences = text.split(/[.!?]+\s*/);
    const categoryScores = new Map<string, number>();

    categories.forEach((category) => {
      const config = this.enhancedCategoryKeywords[category.slug];
      let score = 0;

      if (config) {
        config.keywords.forEach((keyword: WeightedKeyword) => {
          const regex = new RegExp(
            `\\b${keyword.phrase.toLowerCase()}\\b`,
            "g"
          );
          const matches = text.match(regex);
          if (matches) {
            const relevantSentences = sentences.filter((sentence) =>
              this.hasWordMatch(sentence, keyword.phrase.toLowerCase())
            );
            score += relevantSentences.length * keyword.weight;
          }
        });
      }

      categoryScores.set(category.slug, score);
    });

    const mainCategory = Array.from(categoryScores.entries()).sort(
      (a, b) => b[1] - a[1]
    )[0];

    return { categoryScores, mainCategory };
  }

  private analyzeTagScores(
    content: string,
    tagHandling: TagHandling,
    categoryKeywords?: Set<string>
  ): { tagScores: Map<string, number>; mainTagGroup: [string, number] } {
    const text = content.toLowerCase();
    const sentences = text.split(/[.!?]+\s*/);
    const allowedTagGroups = tagHandling["allowedTagGroups"];
    const tagScores = new Map<string, number>();
    const groupScores = new Map<string, number>();

    Object.entries(allowedTagGroups).forEach(([groupType, tags]) => {
      let groupScore = 0;

      tags.forEach(({ tag }: Tag) => {
        const tagLower = tag.toLowerCase();
        let score = 0;
        const weight = tagHandling.getTagWeight(tag);

        if (this.hasWordMatch(text, tagLower)) {
          const relevantSentences = sentences.filter((sentence) =>
            this.hasWordMatch(sentence, tagLower)
          );

          score = 3 * weight * Math.min(relevantSentences.length, 3);

          if (categoryKeywords) {
            const hasContextInSentences = relevantSentences.some((sentence) =>
              Array.from(categoryKeywords).some((keyword) =>
                this.hasWordMatch(sentence, keyword)
              )
            );
            if (hasContextInSentences) {
              score *= 1.2;
            }
          }
        } else if (
          sentences.some((sentence) =>
            tagLower
              .split(" ")
              .every((word) => this.hasWordMatch(sentence, word))
          )
        ) {
          score = 2 * weight;
        } else if (
          tagLower
            .split(" ")
            .some((word) =>
              sentences.some((sentence) => this.hasWordMatch(sentence, word))
            )
        ) {
          score = weight;
        }

        if (score > 0) {
          tagScores.set(tag, score);
          groupScore += score;
        }
      });

      if (groupScore > 0) {
        groupScores.set(groupType, groupScore);
      }
    });

    const mainTagGroup = Array.from(groupScores.entries()).sort(
      (a, b) => b[1] - a[1]
    )[0];

    return { tagScores, mainTagGroup };
  }

  public analyzeContent(
    content: string,
    categories: Category[],
    enhancedCategoryKeywords: Record<string, CategoryConfig>,
    tagHandling: TagHandling
  ): ContentAnalysisResult {
    const { categoryScores, mainCategory } = this.analyzeCategoryScores(
      content,
      categories
    );

    let categoryKeywords: Set<string> | undefined;
    if (mainCategory && mainCategory[1] > 2) {
      const config = enhancedCategoryKeywords[mainCategory[0]];
      categoryKeywords = new Set(
        config?.keywords.map((k) => k.phrase.toLowerCase()) || []
      );
    }

    const { tagScores, mainTagGroup } = this.analyzeTagScores(
      content,
      tagHandling,
      categoryKeywords
    );

    return {
      categoryScores,
      tagScores,
      mainCategory,
      mainTagGroup,
    };
  }

  public getContentSimilarityScore(
    content: T,
    currentContent: T
  ): ContentScore<T> {
    let score = 0;
    let diversityScore = 0;

    if (content.category === currentContent.category) {
      score += 3;
    } else {
      const contentCategory = content.category || "";
      const currentContentCategory = currentContent.category || "";

      const contentConfig = this.enhancedCategoryKeywords[contentCategory];
      const currentContentConfig =
        this.enhancedCategoryKeywords[currentContentCategory];

      const commonKeywords =
        contentConfig?.keywords.filter((keyword: WeightedKeyword) =>
          currentContentConfig?.keywords.some(
            (k: WeightedKeyword) => k.phrase === keyword.phrase
          )
        ).length || 0;

      if (commonKeywords > 0) {
        score += commonKeywords * 0.5;
      }
    }

    if (content.tags && currentContent.tags) {
      const matchingTags = content.tags.filter((tag) =>
        currentContent.tags?.includes(tag)
      );

      matchingTags.forEach((tag) => {
        score += 1.5 * this.tagHandling.getTagWeight(tag);
      });

      const uniqueTags = content.tags.filter(
        (tag) => !currentContent.tags?.includes(tag)
      );

      uniqueTags.forEach((tag) => {
        diversityScore += 0.5 * this.tagHandling.getTagWeight(tag);
      });
    }

    const daysDifference = Math.abs(
      (this.getTimeInDays(this.toDate(content?.createdOn)) -
        this.getTimeInDays(this.toDate(currentContent?.createdOn))) /
        (1000 * 60 * 60 * 24)
    );

    if (daysDifference <= 7) {
      score += 2;
    } else if (daysDifference <= 30) {
      score += 1;
    } else if (daysDifference <= 90) {
      score += 0.5;
    }

    return { content: content, score, diversityScore };
  }
}
